import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useUserProfile } from './useUserProfile';
import { useProduct } from '@/contexts/ProductContext';
import { toast } from 'sonner';

export interface BusinessGoal {
  id: string;
  tenant_id: string;
  goal_type: 'mrr' | 'arr' | 'customers' | 'churn';
  target_value: number;
  period: 'daily' | 'monthly' | 'quarterly' | 'semiannual' | 'yearly';
  year: number;
  month: number | null;
  quarter: number | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
  product_id?: string | null;
  currency: 'BRL' | 'USD';
  product?: {
    id: string;
    name: string;
    logo_url: string | null;
  } | null;
  creator?: {
    user_id: string;
    full_name: string | null;
  } | null;
  updater?: {
    user_id: string;
    full_name: string | null;
  } | null;
}

export type GoalStatus = 'active' | 'future' | 'expired';

export function getGoalStatus(goal: BusinessGoal): GoalStatus {
  const today = new Date().toISOString().split('T')[0];
  if (goal.start_date <= today && goal.end_date >= today) {
    return 'active';
  }
  if (goal.start_date > today) {
    return 'future';
  }
  return 'expired';
}

export function checkDateOverlap(
  goals: BusinessGoal[] | undefined,
  startDate: string,
  endDate: string,
  goalType: BusinessGoal['goal_type'],
  productId: string | null,
  excludeGoalId?: string
): boolean {
  if (!goals) return false;
  
  return goals.some((goal) => {
    if (excludeGoalId && goal.id === excludeGoalId) return false;
    if (goal.goal_type !== goalType) return false;
    if (goal.product_id !== productId) return false;
    if (!goal.is_active) return false;
    
    // Check if date ranges overlap
    return (
      (startDate >= goal.start_date && startDate <= goal.end_date) ||
      (endDate >= goal.start_date && endDate <= goal.end_date) ||
      (goal.start_date >= startDate && goal.start_date <= endDate) ||
      (goal.end_date >= startDate && goal.end_date <= endDate)
    );
  });
}

export function useBusinessGoals() {
  const { user } = useAuth();
  const { tenant } = useUserProfile();
  const { selectedProduct } = useProduct();
  const queryClient = useQueryClient();

  const { data: goals, isLoading } = useQuery({
    queryKey: ['business_goals', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return [];
      
      const { data, error } = await supabase
        .from('business_goals')
        .select(`
          *,
          product:products(id, name, logo_url)
        `)
        .eq('tenant_id', tenant.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Fetch creator and updater profiles separately
      const userIds = [...new Set([
        ...data.map(g => g.created_by),
        ...data.map(g => g.updated_by)
      ].filter(Boolean))] as string[];
      
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name')
          .in('user_id', userIds);
        
        // Combine data with profiles
        const goalsWithProfiles = data.map(goal => ({
          ...goal,
          creator: profiles?.find(p => p.user_id === goal.created_by) || null,
          updater: profiles?.find(p => p.user_id === goal.updated_by) || null,
        }));
        
        return goalsWithProfiles as BusinessGoal[];
      }
      
      return data.map(goal => ({ ...goal, creator: null, updater: null })) as BusinessGoal[];
    },
    enabled: !!tenant?.id,
  });

  const createGoal = useMutation({
    mutationFn: async (goal: Omit<BusinessGoal, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'tenant_id' | 'updated_by'>) => {
      if (!tenant?.id) throw new Error('Tenant não encontrado');
      
      const { error } = await supabase
        .from('business_goals')
        .insert({
          goal_type: goal.goal_type,
          target_value: goal.target_value,
          period: goal.period,
          year: goal.year,
          month: goal.month,
          quarter: goal.quarter,
          start_date: goal.start_date,
          end_date: goal.end_date,
          is_active: goal.is_active,
          tenant_id: tenant.id,
          product_id: goal.product_id,
          currency: goal.currency,
          created_by: user?.id,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business_goals'] });
      toast.success('Meta criada com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao criar meta: ' + error.message);
    },
  });

  const updateGoal = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<BusinessGoal> }) => {
      const { error } = await supabase
        .from('business_goals')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business_goals'] });
      toast.success('Meta atualizada com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao atualizar meta: ' + error.message);
    },
  });

  const deleteGoal = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('business_goals')
        .update({ is_active: false })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business_goals'] });
      toast.success('Meta removida com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao remover meta: ' + error.message);
    },
  });

  return {
    goals,
    isLoading,
    createGoal,
    updateGoal,
    deleteGoal,
  };
}
